"use client";

import { ChevronLeft, Eye, X } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { UserMenu } from "./user-menu";
import { useAuth } from "@/contexts/auth-context";

interface PageHeaderProps {
  title: string;
  onMenuClick?: () => void;
  backHref?: string;
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  onMenuClick,
  backHref,
  actions,
  className,
}: PageHeaderProps) {
  const { isImpersonating, impersonatedRole, impersonatedUser, exitImpersonation } = useAuth();

  const roleLabels: Record<string, string> = {
    admin: "Administrador",
    cashier: "Caixa",
    kitchen: "Cozinha",
    waiter: "Garçom",
    bartender: "Bartender",
    freelancer: "Freelancer",
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-40 flex items-center justify-between h-16 px-5 bg-background",
        className
      )}
    >
      {/* Botão esquerdo: voltar ou menu */}
      {backHref ? (
        <Link
          href={backHref}
          className="flex size-10 items-center justify-center rounded-lg border border-border bg-secondary text-muted-foreground transition-colors hover:bg-muted active:bg-muted/70"
          aria-label="Voltar"
        >
          <ChevronLeft className="size-5" />
        </Link>
      ) : (
        <button
          onClick={onMenuClick}
          className="flex size-10 flex-col items-center justify-center gap-[4px] rounded-lg border border-border bg-secondary px-[9px] py-[11px] transition-colors hover:bg-muted active:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          aria-label="Abrir menu"
        >
          <span className="block h-[2px] w-full rounded-sm bg-foreground" />
          <span className="block h-[2px] w-[70%] self-start rounded-sm bg-foreground" />
          <span className="block h-[2px] w-full rounded-sm bg-foreground" />
        </button>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="text-lg font-semibold tracking-tight text-foreground"
        >
          {title}
        </button>
        {isImpersonating && impersonatedRole && (
          <div
            className="flex min-w-0 items-center gap-1 rounded-full bg-amber-900/30 px-2 py-0.5 text-sm font-medium text-amber-400"
            title={`Vendo como ${impersonatedUser ? impersonatedUser.name : roleLabels[impersonatedRole] || impersonatedRole}`}
          >
            <Eye className="size-3 shrink-0" />
            <span className="max-w-[30vw] truncate">
              {impersonatedUser ? impersonatedUser.name : roleLabels[impersonatedRole] || impersonatedRole}
            </span>
            <button
              onClick={exitImpersonation}
              className="ml-1 shrink-0 rounded-full p-0.5 hover:bg-amber-800/50 transition-colors"
              title="Sair do modo visualização"
            >
              <X className="size-3" />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {actions}
        <UserMenu />
      </div>
    </header>
  );
}
